/**
 * ProtectedRoute - redirects to /login if no valid Cognito session.
 * After login, bootstraps user profile + org/entity data from API.
 *
 * Resilience model (root-cause fix for the "Sign-in failed" recurring banner):
 *   - Zustand store is now persisted to localStorage. On reload we trust the
 *     cached user immediately and revalidate /auth/me in the background.
 *   - Bootstrap distinguishes TRUE auth failures (401 with refresh failure)
 *     from TRANSIENT failures (TypeError, 5xx, timeout, NetworkError). Auth
 *     failures sign out; transient failures DO NOT - they retry with
 *     exponential backoff and, if all retries fail, leave the user on the
 *     last cached state with a non-fatal banner.
 *   - Visibility-change re-check is gated so it doesn't pile retries onto a
 *     bootstrap already in flight.
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, signOut, getAccessToken } from '@/lib/auth';
import { SpinnerRing } from '@/components/ui/AsyncGuard';
import { useAppStore } from '@/stores/useAppStore';
import { apiRequest, api } from '@/lib/api';
import { setupOnlineSync } from '@/lib/offline-queue';
import { AppError, NetworkError, AuthError } from '@/lib/errors';
import type { User, Organization, Entity, EntityMembership, Expense } from '@/types';

interface BootstrapResponse {
  user: User;
  organization: Organization;
  entities: Entity[];
  memberships: EntityMembership[];
}

const MAX_BOOTSTRAP_RETRIES = 3;
const BACKOFF_MS = [1000, 2500, 5000]; // 1s, 2.5s, 5s

// Classify the error so bootstrap knows whether to sign out (real auth
// failure) or retry/preserve cached state (transient backend hiccup).
// AppError uses semantic .code values, not HTTP status codes.
function isTransient(err: unknown): boolean {
  if (err instanceof NetworkError) return true; // includes httpToAppError(503)
  if (err instanceof AppError) {
    // 5xx maps to code 'UNKNOWN' with severity 'critical'; 429 to 'RATE_LIMITED'.
    // Treat both as transient. AUTH_*, FORBIDDEN, NOT_FOUND, VALIDATION_ERROR are not.
    return err.code === 'UNKNOWN' || err.code === 'RATE_LIMITED';
  }
  if (err instanceof TypeError) return true; // raw fetch TypeError (CORS, DNS, brief network blip)
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  return false;
}

function isAuthFailure(err: unknown): boolean {
  if (err instanceof AuthError) return true;
  if (err instanceof AppError && err.code === 'FORBIDDEN') return true;
  return false;
}

function delay(ms: number) { return new Promise(resolve => setTimeout(resolve, ms)); }

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const cachedUser = useAppStore((s) => s.user);
  // Start in 'authenticated' if we have a cached user AND tokens. The
  // background revalidation may overwrite store state, but the user
  // never sees a loading spinner on a normal reload.
  const initialStatus: 'checking' | 'authenticated' | 'unauthenticated' = (() => {
    if (typeof window === 'undefined') return 'checking';
    if (cachedUser && isAuthenticated()) return 'authenticated';
    if (!isAuthenticated()) return 'unauthenticated';
    return 'checking';
  })();
  const [status, setStatus] = useState<'checking' | 'authenticated' | 'unauthenticated'>(initialStatus);
  const [transientWarning, setTransientWarning] = useState<string | null>(null);
  const bootstrapRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);
  const location = useLocation();
  const entities = useAppStore((s) => s.entities);

  const bootstrap = useCallback(async () => {
    if (bootstrapRef.current) return;
    bootstrapRef.current = true;

    const store = useAppStore.getState();
    console.info('[bootstrap] start', { hasUser: !!store.user, authed: isAuthenticated() });

    if (!isAuthenticated()) {
      console.info('[bootstrap] not authenticated - redirecting to /login');
      setStatus('unauthenticated');
      bootstrapRef.current = false;
      return;
    }

    // Pre-flight: ensure we have a valid token before hitting the API.
    // If Cognito refresh fails outright, that's a real auth failure - sign out.
    const token = await getAccessToken();
    if (!token) {
      console.warn('[bootstrap] no valid token after refresh - signing out');
      try { localStorage.setItem('nexus_bootstrap_error', 'Session expired or refresh failed. Please sign in again.'); } catch { /* */ }
      signOut();
      setStatus('unauthenticated');
      bootstrapRef.current = false;
      return;
    }

    // If we already have a cached user, the page is renderable immediately.
    // Run the /auth/me revalidation in the background; failures here are
    // non-fatal (we keep the cached state).
    const hasCachedUser = !!store.user;

    let lastErr: unknown = null;
    let succeeded = false;
    for (let attempt = 0; attempt < MAX_BOOTSTRAP_RETRIES; attempt++) {
      try {
        console.info(`[bootstrap] /auth/me attempt ${attempt + 1}`);
        const data = await apiRequest<BootstrapResponse>('/auth/me', { skipEntityHeader: true });
        console.info('[bootstrap] /auth/me succeeded', {
          user: data.user?.email,
          entities: data.entities?.length,
          memberships: data.memberships?.length,
        });

        if (!data.user || !Array.isArray(data.entities)) {
          throw new Error('Malformed /auth/me response: missing user or entities');
        }

        const { setUser, setActiveOrg, setActiveEntity, setEntities, setEntityMemberships } = useAppStore.getState();
        setUser(data.user);
        setActiveOrg(data.organization);
        setEntities(data.entities);
        setEntityMemberships(data.memberships);

        // Restore active entity from localStorage or use first
        const savedEntityId = localStorage.getItem('nexus_active_entity');
        const activeEntity = data.entities.find(e => e.id === savedEntityId) || data.entities[0];
        if (activeEntity) {
          setActiveEntity(activeEntity);
        } else {
          console.warn('[bootstrap] user has no entities - some UI may be limited');
        }

        setStatus('authenticated');
        setTransientWarning(null);
        // Wire offline sync (outside try; failure here shouldn't bounce the user)
        try {
          cleanupRef.current = setupOnlineSync(async (expense: Partial<Expense>) => {
            try {
              await api.createExpense(expense as Record<string, unknown>);
              return true;
            } catch { return false; }
          });
        } catch (syncErr) {
          console.warn('[bootstrap] setupOnlineSync failed (non-fatal):', syncErr);
        }
        succeeded = true;
        break;
      } catch (err) {
        lastErr = err;
        const errName = err instanceof Error ? err.name : typeof err;
        const errMsg = err instanceof Error ? err.message : String(err);
        console.warn(`[bootstrap] attempt ${attempt + 1} failed:`, errName, errMsg);

        if (isAuthFailure(err)) {
          // Real auth failure - bail immediately, don't retry.
          console.error('[bootstrap] auth failure - signing out');
          break;
        }

        if (!isTransient(err)) {
          // Unknown error class - log and bail without sign-out.
          console.error('[bootstrap] non-transient unknown error - leaving cached state alone');
          break;
        }

        // Transient: backoff and retry
        if (attempt < MAX_BOOTSTRAP_RETRIES - 1) {
          await delay(BACKOFF_MS[attempt]);
        }
      }
    }

    if (!succeeded) {
      // Final state branches:
      //   - real auth failure (401/403): sign out + go to /login
      //   - transient (network/5xx/timeout) AND we have cached user: stay
      //     signed in, render the cached app, surface a non-fatal banner
      //   - transient AND no cached user: send to /login with banner
      if (isAuthFailure(lastErr)) {
        try { localStorage.setItem('nexus_bootstrap_error', 'Session expired. Please sign in again.'); } catch { /* */ }
        signOut();
        setStatus('unauthenticated');
      } else if (hasCachedUser) {
        console.warn('[bootstrap] all retries failed but cached user available - staying signed in');
        setTransientWarning('Working with cached data - the server is taking longer than usual to respond.');
        setStatus('authenticated');
      } else {
        // No cached state to fall back to - the user did need to bootstrap
        // and we genuinely could not. Surface the issue but DO NOT sign
        // them out (their tokens may still be valid). Send to /login with
        // a banner; if they sign in fresh it'll just re-trigger bootstrap.
        const msg = lastErr instanceof Error ? lastErr.message : String(lastErr);
        try { localStorage.setItem('nexus_bootstrap_error', `Could not load your account: ${msg}`); } catch { /* */ }
        setStatus('unauthenticated');
      }
    }

    bootstrapRef.current = false;
  }, []);

  useEffect(() => {
    bootstrap();

    // Re-check auth when tab becomes visible (handles iOS background/foreground)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && !bootstrapRef.current) {
        const store = useAppStore.getState();
        if (!store.user && isAuthenticated()) {
          bootstrap();
        } else if (!isAuthenticated()) {
          setStatus('unauthenticated');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if (cleanupRef.current) cleanupRef.current();
    };
  }, [bootstrap]);

  if (status === 'checking') {
    return (
      <div className="min-h-dvh bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <SpinnerRing className="w-12 h-12 border-4 mx-auto mb-4" />
          <p className="text-slate-500 dark:text-slate-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    // Unauthenticated visitors land on the public marketing page, not the
    // login form. Login is one click away via the header. This gives cold
    // traffic a reason to stay instead of bouncing.
    return <Navigate to="/welcome" replace />;
  }

  // Bootstrapped but no entities yet -> user hasn't completed onboarding.
  // Force them through the wizard. Don't redirect if they're already there.
  if (entities.length === 0 && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <>
      {transientWarning && (
        <div className="fixed top-0 inset-x-0 z-50 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800/40 px-4 py-2 text-xs text-amber-900 dark:text-amber-200 text-center">
          {transientWarning}
        </div>
      )}
      {children}
    </>
  );
}
